import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { FacebookCampaign } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import { Alert, Button } from '@feathr/components';
import { useStore } from '@feathr/report_components/state';

import * as styles from './FacebookCampaignAlerts.css';

interface IProps {
  campaign: FacebookCampaign;
  localUrl: (url: string) => string;
}

function FacebookCampaignAlerts({ campaign, localUrl }: Readonly<IProps>): JSX.Element {
  const { Creatives } = useStore();
  const { t } = useTranslation();
  const hasUpsyncErrors = campaign.get('state') === CampaignState.Erroring;
  const allTargetSegmentsErroring = campaign.get('all_target_segments_erroring');

  // Check if any creatives are in a state that requires attention
  const rejectedCreatives = Creatives.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
      ad_status__in: ['WITH_ISSUES', 'DISAPPROVED'],
    },
  });

  return (
    <>
      {hasUpsyncErrors && !allTargetSegmentsErroring && (
        <Alert
          className={styles.alert}
          description={
            <Trans t={t}>
              Please{' '}
              <Button
                href={localUrl(`${campaign.getItemUrl('edit')}`)}
                target={'_blank'}
                type={'link'}
              >
                make the recommended edits to your campaign
              </Button>{' '}
              and publish it again.
            </Trans>
          }
          title={t('Problem syncing campaign with Meta')}
          type={'danger'}
        />
      )}
      {!rejectedCreatives.isPending && !!rejectedCreatives.models.length && (
        <Alert
          className={styles.alert}
          description={
            <>
              {t('Please ')}
              <Link to={localUrl(`${campaign.getItemUrl()}/edit#step3`)}>
                {t("review this campaign's ads")}
              </Link>
              {t(' to resolve the issue.')}
            </>
          }
          title={t('One or more ads in this campaign have been rejected')}
          type={'danger'}
        />
      )}
      {allTargetSegmentsErroring && (
        <Alert
          className={styles.alert}
          description={
            <Link to={localUrl(`${campaign.getItemUrl()}/edit#step2`)}>
              {t('View and manage your campaign targets.')}
            </Link>
          }
          title={t('All target audiences in this campaign are too small to target')}
          type={'danger'}
        />
      )}
    </>
  );
}

export default observer(FacebookCampaignAlerts);
